import { ToastContainer } from "react-toastify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../actions/auth";
import {
  API_ENVIRONMENTS,
  getApiEnvironment,
  setApiEnvironment,
} from "../services/apiConfig";
import { refreshApiClient } from "../services/api";
import useLoginStyles from "./loginStyles";

const environmentOptions = Object.entries(API_ENVIRONMENTS).map(([value, config]) => ({
  value,
  label: config.label,
}));

const Login = () => {
  const { classes } = useLoginStyles();
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [environment, setEnvironment] = useState(getApiEnvironment());
  const [showPassword, setShowPassword] = useState(false);
  
  const history = useNavigate();
  const { isLoggedIn, token, loginError } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    refreshApiClient();
  }, []);

  const handleEnvironmentChange = (e) => {
    const value = e.target.value;
    if (!value || !API_ENVIRONMENTS[value]) return;
    setApiEnvironment(value);
    refreshApiClient();
    setEnvironment(value);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!username || password.length < 3) return;
    
    setApiEnvironment(environment);
    refreshApiClient();
    setLoading(true);
    dispatch(login({ username, password }));
  };

  useEffect(() => {
    const userToken = localStorage.getItem("user_token");
    const userRole = localStorage.getItem("user_role");
    if (isLoggedIn) {
      if (userToken != null) {
        if (userRole === "admin") {
          history("/admin/dashboard", {
            replace: true,
            state: { token },
          });
        } else if (
          userRole === "content_feeder" ||
          userRole === "content_validator"
        ) {
          history("/cm/dashboard", { replace: true });
        }
      }
    }
    if (loginError) {
      setLoading(false);
    }
  }, [isLoggedIn, loginError, history, token]);

  return (
    <div className={classes.root}>
      <div className={classes.brandPanel}>
        <div>
          <img 
            src="https://staging.affordamic.com/_next/static/media/header_logo.6c5c8ec2.svg" 
            alt="Affordamic Logo" 
            className={classes.brandLogo} 
          />
          <h1 className={classes.brandTitle}>CMS Portal</h1>
          <p className={classes.brandSubtitle}>
            Manage exams, content, and operations from one secure workspace.
          </p>
        </div>
      </div>

      <div className={classes.formPanel}>
        <div className={classes.formCard}>
          <h2 className={classes.heading}>Welcome back</h2>
          <p className={classes.subheading}>
            Sign in with your employee credentials
          </p>

          <form onSubmit={onSubmit}>
            <div className={classes.formGroup}>
              <label className={classes.label}>Username or email</label>
              <input
                type="text"
                placeholder="you@affordamic.com"
                className={classes.input}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label className={classes.label}>Password</label>
              <div className={classes.passwordWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className={classes.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className={classes.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className={classes.formGroup}>
              <label className={classes.label}>Login as</label>
              <select
                className={classes.select}
                value={environment}
                onChange={handleEnvironmentChange}
              >
                {environmentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {loginError && (
              <div className={classes.errorBox}>
                <span>{loginError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={classes.submitButton}
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Login;
